import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, Polygon, OverlayView, useLoadScript, DrawingManager } from "@react-google-maps/api";
import mapStyle from "../../data/map.json";

const GMap = ({ polygons, onCoordinatesChange }) => {
    const mapRef = useRef(null);
    const drawnPolygonsRef = useRef([]); // To keep track of drawn polygons
    const [drawnPolygons, setDrawnPolygons] = useState([]);
    const defaultCenter = { lat: -15.4510304, lng: 28.4576441 }; // Set a default center
    const defaultZoom = 10; // Set a default zoom level

    const onLoad = useCallback(map => {
        mapRef.current = map;

        const undoButtonDiv = document.createElement('div');
        const undoButton = document.createElement('div');
        undoButton.innerHTML = '&#x21A9;';

        undoButton.style.backgroundColor = '#fff';
        undoButton.style.top = '5px';
        undoButton.style.border = '2px solid #fff';
        undoButton.style.borderRadius = '3px';
        undoButton.style.cursor = 'pointer';
        undoButton.style.fontSize = '18px';
        undoButton.style.display = 'inline-flex';
        undoButton.style.alignItems = 'center';
        undoButton.style.justifyContent = 'center';
        undoButton.style.width = '24px';
        undoButton.style.height = '24px';
        undoButton.style.margin = '5px 0 0 0px';
        undoButton.style.userSelect = 'none';
        undoButton.addEventListener('click', handleUndo);
        undoButtonDiv.appendChild(undoButton);
        map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(undoButtonDiv);

        if (polygons && polygons.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            polygons.forEach(polygon => {
                polygon.coordinates.forEach(coord => {
                    bounds.extend(coord);
                });
            });
            map.fitBounds(bounds);
        } else {
            map.setCenter(defaultCenter);
            map.setZoom(defaultZoom);
        }
    }, [polygons]);

    useEffect(() => {
        if (mapRef.current && polygons && polygons.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            polygons.forEach(polygon => {
                polygon.coordinates.forEach(coord => {
                    bounds.extend(coord);
                });
            });
            mapRef.current.fitBounds(bounds);
        } else if (mapRef.current) {
            mapRef.current.setCenter(defaultCenter);
            mapRef.current.setZoom(defaultZoom);
        }
    }, [polygons]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY,
        libraries: ["drawing"]
    });

    const options = {
        drawingControl: true,
        drawingControlOptions: {
            drawingModes: ['polygon']
        },
        polygonOptions: {
            fillColor: '#FD2311',
            strokeColor: '#FD2311',
            fillOpacity: 0.5,
            strokeWeight: 3,
            clickable: true,
            editable: true, // Enable editing
            draggable: true,
            zIndex: 1
        }
    };

    const calculateCenter = (coordinates) => {
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach(coord => bounds.extend(coord));
        return bounds.getCenter().toJSON();
    };

    const updateDrawnPolygons = () => {
        const updatedDrawnPolygons = drawnPolygonsRef.current.map(poly => {
            const polyArray = poly.getPath().getArray();
            return polyArray.map(path => ({ lat: path.lat(), lng: path.lng() }));
        });
        onCoordinatesChange(updatedDrawnPolygons);
    };

    const onPolygonComplete = useCallback(poly => {
        drawnPolygonsRef.current.push(poly);
        setDrawnPolygons([...drawnPolygonsRef.current]); // Update the state to reflect changes
        updateDrawnPolygons();

        // Add event listeners to update coordinates when the drawn polygon is edited
        poly.getPath().addListener('set_at', updateDrawnPolygons);
        poly.getPath().addListener('insert_at', updateDrawnPolygons);
        poly.getPath().addListener('remove_at', updateDrawnPolygons);
    }, [onCoordinatesChange]);

    const handleUndo = () => {
        if (drawnPolygonsRef.current.length > 0) {
            const lastPolygon = drawnPolygonsRef.current.pop();
            lastPolygon.setMap(null); // Remove the polygon from the map
            setDrawnPolygons([...drawnPolygonsRef.current]); // Update the state
            updateDrawnPolygons();
        }
    };

    return (
        <>
            {!isLoaded ? (
                <h6 style={{ marginTop: 40 }}>Loading Map...</h6>
            ) : (
                <div>
                    <GoogleMap
                        mapContainerClassName="map-container"
                        onLoad={onLoad}
                        options={{ styles: mapStyle }}
                    >
                        {/* Render existing polygons */}
                        {polygons && polygons.length > 0 && polygons.map((polygon, index) => (
                            <React.Fragment key={index}>
                                <Polygon
                                    paths={polygon.coordinates}
                                    options={{
                                        fillColor: polygon.fillColor,
                                        fillOpacity: 0.35,
                                        strokeColor: "#FF0000",
                                        strokeOpacity: 0.35,
                                        strokeWeight: 2.5,
                                        editable: false,
                                        draggable: false
                                    }}
                                />
                                <OverlayView
                                    position={calculateCenter(polygon.coordinates)}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        backgroundColor: 'white',
                                        padding: '5px 10px',
                                        borderRadius: '8px',
                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <h6 style={{
                                            margin: 0,
                                            padding: 0,
                                            fontSize: '14px'
                                        }}>
                                            {polygon.name}
                                        </h6>
                                        <div className="d-flex align-items-center">
                                            <div className="avatar wd-20 ht-20"><img src={polygon.logo} className="rounded-circle" alt="logo"/></div>
                                            <span className="tx-color-0 tx-14 pd-x-5">{polygon.collector}</span>
                                        </div>
                                    </div>
                                </OverlayView>
                            </React.Fragment>
                        ))}
                        <DrawingManager
                            options={options}
                            onPolygonComplete={onPolygonComplete}
                        />
                    </GoogleMap>
                </div>
            )}
        </>
    );
};

export default GMap;
